import { useState } from 'react';
import { useEffect, useRef } from 'react';
import { addMonths, startOfWeek } from 'date-fns';
import { Box, Dialog } from '@mui/material';
import MobileWeekStrip from './MobileWeekStrip';
import MobileMonthCalendar from './MobileMonthCalendar';

const MobileWeekChrome = ({
  weekStart,
  selectedDay,
  onSelectDay,
  onPreviousWeek,
  onNextWeek,
  visibleMonth,
  availabilities,
  offeredAllocations,
  openAllocations,
  indicatorDateKeys,
  onJumpToDay,
  sticky = true,
  stripRef: stripRefProp,
}) => {
  const localStripRef = useRef(null);
  const stripRef = stripRefProp || localStripRef;
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(visibleMonth);

  useEffect(() => {
    const header = document.querySelector('.glass-app-header');
    if (!header) return undefined;

    const syncStickyChrome = () => {
      const headerHeight = header.offsetHeight;
      const stripHeight = stripRef.current?.offsetHeight || 0;
      document.documentElement.style.setProperty(
        '--glass-sticky-header-offset',
        `${headerHeight}px`
      );
      document.documentElement.style.setProperty(
        '--glass-day-scroll-margin',
        `${headerHeight + stripHeight + 8}px`
      );
    };

    syncStickyChrome();
    window.addEventListener('resize', syncStickyChrome);
    return () => window.removeEventListener('resize', syncStickyChrome);
  }, [stripRef]);

  const openMonthPicker = () => {
    setPickerMonth(visibleMonth);
    setMonthPickerOpen(true);
  };

  const closeMonthPicker = () => setMonthPickerOpen(false);

  const handlePickerSelectDay = (day) => {
    onJumpToDay(day);
    closeMonthPicker();
  };

  const handlePreviousMonth = () => setPickerMonth((month) => addMonths(month, -1));
  const handleNextMonth = () => setPickerMonth((month) => addMonths(month, 1));

  return (
    <>
      <MobileWeekStrip
        weekStart={weekStart}
        selectedDay={selectedDay}
        onSelectDay={onSelectDay}
        onPreviousWeek={onPreviousWeek}
        onNextWeek={onNextWeek}
        sticky={sticky}
        stripRef={stripRef}
        onOpenMonthPicker={openMonthPicker}
      />

      <Dialog
        open={monthPickerOpen}
        onClose={closeMonthPicker}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          className: 'glass-surface-accent glass-modal',
          sx: { borderRadius: '16px', m: 1 },
        }}
        BackdropProps={{
          sx: { backgroundColor: 'rgba(6, 6, 8, 0.65)', backdropFilter: 'blur(8px)' },
        }}
      >
        <Box sx={{ p: 1.5 }}>
          <MobileMonthCalendar
            visibleMonth={pickerMonth}
            selectedDay={selectedDay}
            availabilities={availabilities}
            offeredAllocations={offeredAllocations}
            openAllocations={openAllocations}
            indicatorDateKeys={indicatorDateKeys}
            onSelectDay={handlePickerSelectDay}
            onPreviousMonth={handlePreviousMonth}
            onNextMonth={handleNextMonth}
          />
        </Box>
      </Dialog>
    </>
  );
};

export const jumpWeekToDay = (day, setWeekStart, setSelectedDay) => {
  const monday = startOfWeek(day, { weekStartsOn: 1 });
  const normalized = new Date(day);
  normalized.setHours(0, 0, 0, 0);
  setWeekStart(monday);
  setSelectedDay(normalized);
};

export default MobileWeekChrome;
